import React, { useState, useEffect, useCallback } from "react";
import {
  ScrollView,
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { useCameraPermissions } from "expo-camera";
import { Toast } from "react-native-toast-notifications";
import customAxios from "../../../utils/customAxios";
import i18n from "../../../locales/i18n";
import { useAudioPlayer } from "expo-audio";
import { useCart } from "../../../hooks/useCart";
import { useDispatch, useSelector } from "react-redux";
import { appActions } from "../../../store/slices/appSlice";
import { RootState } from "../../../store";
import CameraScanner from "../../../components/scanner/CameraScanner";
import QuantityInput from "../../../components/scanner/QuantityInput";
import ProductList from "../../../components/scanner/ProductList";
import OtherProductInput from "../../../components/scanner/OtherProductInput";
import BillModal from "../../../components/scanner/BillModal";
import ActionButtons from "../../../components/scanner/ActionButtons";
import TotalDisplay from "../../../components/scanner/TotalDisplay";
import { router, useFocusEffect } from "expo-router";
import AntDesign from "@expo/vector-icons/AntDesign";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import { SafeAreaView } from "react-native-safe-area-context";
import Animated, { FadeInDown } from "react-native-reanimated";

interface Product {
  id_product: number;
  name: string;
  price_sell: string;
  price_bought: string;
  stock: number;
  photo?: string;
  quantity?: number;
}

export default function Scanner() {
  const dispatch = useDispatch();
  const { sellWithScanner, locale } = useSelector(
    (state: RootState) => state.app
  );

  // Use Redux-based cart for persistent cart data
  const {
    selectedProducts,
    quantities,
    totalPrice,
    boughtPrice,
    addProduct,
    updateQuantity,
    removeProduct,
    clearCart,
    calculateTotals,
    setSelectedProducts,
    setQuantities,
    setTotalPrice,
  } = useCart();

  const [isChecked, setChecked] = useState(false);
  const [permission, requestPermission] = useCameraPermissions();
  const [buttonSpinner, setButtonSpinner] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedTag, setSelectedTag] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [OpenModal, setOpenModel] = useState(false);
  const [ShowmoRe, SetShowMore] = useState(true);
  const [buttonSpinner2, setButtonSpinner2] = useState(false);
  const [time, SetTimeResponse] = useState("");
  const [date, SetDateResponse] = useState("");
  const [scanning, setScanning] = useState(false);
  const audioPlayer = useAudioPlayer(
    require("../../../assets/sounds/scan.mp3")
  );
  const [autherItem, setAutherItem] = useState("");
  const [billData, setBillData] = useState<any>({});
  const [OpenCam, SetOpenCam] = useState(true);
  const [totalOther, setTotalOther] = useState(0);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [custumQunatity, setCustumQuantity] = useState("");
  const [factureId, setFactureId] = useState<number | null>(null);
  const [soldTotal, setSoldTotal] = useState<number>(0);
  let allproducts: any[] = [];

  // Update locale when screen is focused
  useFocusEffect(
    useCallback(() => {
      dispatch(appActions.setLocale(i18n.locale));
    }, [dispatch])
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery) {
        handelSearchProduct(searchQuery);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  useEffect(() => {
    calculateTotals();
  }, [quantities, selectedProducts, calculateTotals]);

  const handelSearchProduct = async (productName: string) => {
    setButtonSpinner(true);
    try {
      const res = await customAxios.get(`/sell/search?product=${productName}`);

      setButtonSpinner(false);
      setProducts(res.data.length > 0 ? res.data : []);
    } catch (error) {
      setButtonSpinner(false);
    }
  };

  const handelAddProduct = () => {
    // Handle custom quantity for selected product
    if (custumQunatity && selectedTag) {
      const quantity = parseFloat(custumQunatity);
      if (quantity > 0) {
        updateQuantity(selectedTag, quantity);
        setCustumQuantity("");
        Toast.show(i18n.t("quantityUpdated"), {
          type: "success",
        });
        return;
      } else {
        Toast.show(i18n.t("enterValidQuantity"), {
          type: "danger",
        });
        return;
      }
    }

    // Handle adding "other" item with custom price
    if (autherItem && autherItem.match(/^-?\d*\.?\d+$/)) {
      const uniqueId = Date.now();
      const quantity = custumQunatity ? parseFloat(custumQunatity) : 1;

      if (custumQunatity && quantity <= 0) {
        Toast.show(i18n.t("enterValidQuantity"), {
          type: "danger",
        });
        return;
      }

      allproducts.push({
        id_product: 0,
        quantity: quantity,
        product_name: "other",
      });

      const otherProduct = {
        id_product: uniqueId,
        quantity: quantity,
        name: "other",
        price_bought: "0",
        price_sell: autherItem,
        stock: 1,
      };

      addProduct(otherProduct, quantity);

      Toast.show(i18n.t("productAddedSuccess"), {
        type: "success",
      });
      setTotalOther((prev) => prev + +autherItem * quantity);
      setAutherItem("");
      setCustumQuantity("");
      return;
    }

    if (autherItem && !autherItem.match(/^[0-9]+$/)) {
      Toast.show(i18n.t("enterValidValue"), {
        type: "danger",
      });
    }
  };

  const removeTag = (index: number) => {
    const productToRemove = selectedProducts[index];
    if (productToRemove) {
      removeProduct(productToRemove.id_product);
      if (selectedTag === productToRemove.id_product) {
        setSelectedTag(null);
      }
    }
  };

  const handleSelect = (item: Product) => {
    // Check if product already exists in the selected list
    const existingProductIndex = selectedProducts.findIndex(
      (prod: any) => prod.id_product === item.id_product
    );

    if (existingProductIndex === -1) {
      // Product doesn't exist, add it
      setSearchQuery(item.name);
      const quantity = custumQunatity ? parseFloat(custumQunatity) : 1;
      addProduct(item, quantity > 0 ? quantity : 1);
      setIsDropdownVisible(false);
      setSearchQuery("");
      setCustumQuantity("");

      Toast.show(`${i18n.t("productAdded")} ${item.name}`, {
        type: "success",
      });
    } else {
      // Product exists, increase quantity
      const quantity = custumQunatity ? parseFloat(custumQunatity) : 1;
      const currentQuantity = quantities[item.id_product] || 1;
      updateQuantity(
        item.id_product,
        currentQuantity + (quantity > 0 ? quantity : 1)
      );
      setSearchQuery("");
      setIsDropdownVisible(false);
      setCustumQuantity("");

      Toast.show(`${i18n.t("quantityUpdated")} ${item.name}`, {
        type: "success",
      });
    }

    setSelectedTag(item.id_product);
  };

  const handleIncrementQuantity = () => {
    if (selectedTag) {
      const currentQuantity = quantities[selectedTag] || 1;
      updateQuantity(selectedTag, currentQuantity + 1);
    }
  };

  const handleDecrementQuantity = () => {
    if (selectedTag) {
      const currentQuantity = quantities[selectedTag] || 1;
      if (currentQuantity > 1) {
        updateQuantity(selectedTag, currentQuantity - 1);
      }
    }
  };

  const handelConfirm = async () => {
    if (selectedProducts.length === 0) {
      Toast.show(i18n.t("searchForProduct"), {
        type: "danger",
      });
      return;
    }
    if (totalPrice < 0) {
      Toast.show(i18n.t("totalCannotBeNegative"), {
        type: "danger",
      });
      return;
    }

    const newProducts = selectedProducts.map((item: any) => {
      if (item.name === "other") {
        return {
          id_product: 0,
          quantity: quantities[item.id_product] || 1,
          product_name: "other",
        };
      } else {
        return {
          id_product: item.id_product,
          quantity: quantities[item.id_product] || 1,
          product_name: item.name,
        };
      }
    });
    setAllProducts(newProducts);
    selectedProducts.forEach((item: any) => {
      if (item.name === "other") {
        allproducts.push({
          id_product: 0,
          price_sell: item.price_sell,
          quantity: quantities[item.id_product] || 1,
          product_name: "other",
        });
      } else {
        allproducts.push({
          id_product: item.id_product,
          price_sell: item.price_sell,
          quantity: quantities[item.id_product] || 1,
          product_name: item.name,
        });
      }
    });

    setAutherItem("");
    setButtonSpinner2(true);
    const profit = selectedProducts.reduce((acc: number, item: any) => {
      if (item.name !== "other") {
        return (
          acc +
          (quantities[item.id_product] || 1) *
            (parseFloat(item.price_sell) - parseFloat(item.price_bought))
        );
      }
      return acc;
    }, 0);

    await customAxios
      .post("/sell/client/checkout", {
        total: totalPrice,
        profit: profit,
        capital: boughtPrice,
        listofproducts: allproducts,
      })
      .then(async (res) => {
        Toast.show(i18n.t("saleSuccess"), { type: "success" });
        setOpenModel(true);
        setButtonSpinner2(false);
        setFactureId(res.data.id_facture);

        // Trigger stats refresh
        dispatch(appActions.toggleStatsRefresh());

        // Store the total before clearing cart (so modal can display it)
        setSoldTotal(totalPrice);

        // Clear cart after successful sale
        clearCart();

        // Reset form state
        setAutherItem("");
        setCustumQuantity("");
        setSelectedTag(null);
        setSearchQuery("");
        setIsDropdownVisible(false);
        setProducts([]);
        setTotalOther(0);
        setAllProducts([]);
        allproducts = [];

        SetOpenCam(false);
        setBillData(res.data);
        if (res.data?.created_at) {
          let dateObj = new Date(res.data.created_at);
          if (!isNaN(dateObj.getTime())) {
            let formattedDate = dateObj.toISOString().split("T")[0];
            let formattedTime = dateObj
              .toISOString()
              .split("T")[1]
              .split(".")[0];
            SetDateResponse(formattedDate);
            SetTimeResponse(formattedTime);
          } else {
            // Invalid date
          }
        } else {
          // created_at is undefined or invalid in response data
        }
      })
      .catch((error) => {
        setButtonSpinner2(false);

        // Check if this is a free tier limit exceeded error (403)
        if (error?.response?.status === 403) {
          const errorMessage = error?.response?.data?.error || "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.toLowerCase().includes("free tier")
          ) {
            // Show upgrade message and navigate to limits page
            Toast.show(
              i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
              {
                type: "danger",
                duration: 5000,
              }
            );

            // Navigate to limits page after a short delay
            setTimeout(() => {
              router.push("/(drawer)/params/limits");
            }, 2000);
            return;
          }
        }

        // Show more specific error message for other errors
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          i18n.t("billNotSaved");
        Toast.show(errorMessage, { type: "danger" });
      });
  };

  function playSound() {
    audioPlayer.play();
  }

  const handelScanProduct = async (code: string) => {
    if (scanning) return;
    setScanning(true);
    playSound();
    try {
      const res = await customAxios.post(`/sell`, { barcode: +code });
      setButtonSpinner(false);
      Toast.show(i18n.t("scanSuccess"), {
        type: "success",
      });
      addProduct(res.data, 1);
      // Add new scanned product to the beginning of the list
      setProducts((prev) => {
        // Remove the item if it already exists to avoid duplicates
        const filteredProducts = prev.filter(
          (p) => p.id_product !== res.data.id_product
        );
        // Add the new/updated item at the beginning
        return [{ ...res.data }, ...filteredProducts];
      });
    } catch (error: any) {
      setButtonSpinner(false);

      // Check if this is a free tier limit exceeded error (403)
      if (error?.response?.status === 403) {
        const errorMessage = error?.response?.data?.error || "";
        if (
          typeof errorMessage === "string" &&
          errorMessage.toLowerCase().includes("free tier")
        ) {
          // Show upgrade message and navigate to limits page
          Toast.show(
            i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
            {
              type: "danger",
              duration: 5000,
            }
          );

          // Navigate to limits page after a short delay
          setTimeout(() => {
            router.push("/(drawer)/params/limits");
          }, 2000);
          return;
        }
      }

      if (error?.response?.status === 404) {
        Toast.show(i18n.t("productNotFound"), {
          type: "danger",
        });
      } else {
        Toast.show(i18n.t("scanError"), {
          type: "danger",
        });
      }
    }
    setTimeout(() => {
      setScanning(false);
    }, 1000);
  };

  // On web, redirect to sell screen (no camera needed)
  if (Platform.OS === "web") {
    // Import and render WebSellScreen directly
    const WebSellScreen =
      require("../../../components/web/WebSellScreen").default;
    return <WebSellScreen />;
  }

  if (!permission) {
    return (
      <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
        <StandardNavigation
          showBackButton={true}
          showMenuButton={true}
          showLogo={true}
        />
        <View className="flex bg-white  flex-1 items-center justify-center px-4">
          <ActivityIndicator size="large" color="green" />
          <Text className="text-xl ">{i18n.t("pleasewait")}</Text>
        </View>
      </View>
    );
  }
  if (!permission.granted) {
    return (
      <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
        <StandardNavigation
          showBackButton={true}
          showMenuButton={true}
          showLogo={true}
        />
        <View className="flex flex-1 bg-white items-center justify-center px-4">
          <TouchableOpacity
            onPress={requestPermission}
            style={{ borderRadius: 9999 }}
          >
            <LinearGradient
              colors={["rgba(19, 121, 162, 1)", "rgba(5, 150, 208, 1)"]}
              style={{
                marginVertical: 16,
                padding: 16,
                borderRadius: 9999,
              }}
            >
              <Text className="text-white text-center  text-xl font-semibold ">
                {i18n.t("grantPermission")}
              </Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      {/* Standard Navigation */}
      <StandardNavigation
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
        shareRoute
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
      >
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ paddingTop: 0 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Animated.View entering={FadeInDown.duration(600).delay(100)}>
            <CameraScanner
              OpenCam={OpenCam}
              SetOpenCam={SetOpenCam}
              handelScanProduct={handelScanProduct}
              scanning={scanning}
              locale={locale}
            />
          </Animated.View>

          <Animated.View entering={FadeInDown.duration(600).delay(200)}>
            <TotalDisplay totalPrice={totalPrice} locale={locale} />
          </Animated.View>

          <Animated.View 
            entering={FadeInDown.duration(600).delay(300)}
            className="w-full items-center justify-center mt-2 px-2"
          >
            <View
              className={`${
                locale == "fr" ? "flex-row-reverse" : "flex-row"
              }   flex items-center justify-center`}
              style={{ maxWidth: 600 }}
            >
              <QuantityInput
                custumQunatity={custumQunatity}
                setCustumQuantity={setCustumQuantity}
                selectedTag={selectedTag}
                handleIncrementQuantity={handleIncrementQuantity}
                handleDecrementQuantity={handleDecrementQuantity}
                handelAddProduct={handelAddProduct}
              />
              <OtherProductInput
                autherItem={autherItem}
                setAutherItem={setAutherItem}
                custumQunatity={custumQunatity}
                handelAddProduct={handelAddProduct}
                locale={locale}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isDropdownVisible={isDropdownVisible}
                setIsDropdownVisible={setIsDropdownVisible}
                products={products}
                buttonSpinner={buttonSpinner}
                handleSelect={handleSelect}
                SetOpenCam={SetOpenCam}
              />
            </View>
          </Animated.View>

          {/* Green Add Button Row */}
          {/* <View className="px-6 mt-4 mb-4">
             ... code omitted ...
          </View> */}

          <Animated.View 
            entering={FadeInDown.duration(600).delay(400)}
            className="w-full px-6"
          >
            <BillModal
              OpenModal={OpenModal}
              factureId={factureId}
              setOpenModel={setOpenModel}
              billData={billData}
              totalPrice={soldTotal}
              time={time}
              date={date}
              ShowmoRe={ShowmoRe}
              SetShowMore={SetShowMore}
              allProducts={allProducts}
              buttonSpinner={buttonSpinner}
              SetOpenCam={SetOpenCam}
              setTotalOther={setTotalOther}
              selectedProduct={selectedProducts}
              setSelectedProducts={setSelectedProducts}
              setProducts={setProducts}
              setAllProducts={setAllProducts}
              locale={locale}
              isChecked={isChecked}
              setChecked={setChecked}
              clearCart={clearCart}
            />
            <ProductList
              selectedProduct={selectedProducts}
              selectedTag={selectedTag}
              setSelectedTag={setSelectedTag}
              quantities={quantities}
              removeTag={removeTag}
            />
            <ActionButtons
              total={totalPrice}
              selectedProduct={selectedProducts}
              isChecked={isChecked}
              setChecked={setChecked}
              buttonSpinner2={buttonSpinner2}
              handelConfirm={handelConfirm}
              locale={locale}
            />
          </Animated.View>

          {/* without scanner  */}
          <Animated.View entering={FadeInDown.duration(600).delay(500)}>
            <TouchableOpacity
            onPress={() => {
              sellWithScanner ? router.push("/scannerwithout") : router.back();
            }}
            className={`${
              locale == "fr"
                ? "flex-row items-end"
                : "flex-row-reverse items-start"
            }
          flex items-center  px-4 my-2 
          `}
          >
            <View className="border border-primaryV2 rounded-xl  p-2">
              <AntDesign name="double-right" size={24} color="#279F9E" />
            </View>

            <Text
              className={`${
                locale == "ar" ? "text-right" : "text-left"
              } text-lg font-semibold px-4`}
              style={{ color: "#279F9E" }}
            >
              {i18n.t("sellWithout")}
            </Text>
            </TouchableOpacity>
          </Animated.View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
